import { useId, useState, type DragEvent } from "react";

import { cn } from "src/lib/utils";
import { Field, type FieldProps } from "./Field";

export interface FileDropzoneProps extends Pick<FieldProps, "label" | "error" | "hint"> {
	/** The currently staged file, if any. The caller owns this state. */
	file?: File | null;
	onFileChange: (file: File | null) => void;
	/** `accept` attribute, e.g. `".xlsx,.csv"`. */
	accept?: string;
	disabled?: boolean;
	className?: string;
	containerClassName?: string;
}

/** Drag-or-click file picker. Replaces the legacy `.drop-zone` / `.file-upload-label` CSS. */
export const FileDropzone = ({
	label,
	error,
	hint,
	file,
	onFileChange,
	accept,
	disabled,
	className,
	containerClassName,
}: FileDropzoneProps) => {
	const inputId = useId();
	const [dragging, setDragging] = useState(false);

	const handleDrop = (event: DragEvent<HTMLLabelElement>) => {
		event.preventDefault();
		setDragging(false);
		if (disabled) return;
		onFileChange(event.dataTransfer.files?.[0] ?? null);
	};

	return (
		<Field label={label} error={error} hint={hint} className={containerClassName}>
			<label
				htmlFor={inputId}
				onDrop={handleDrop}
				onDragOver={(event) => {
					event.preventDefault();
					setDragging(true);
				}}
				onDragLeave={() => setDragging(false)}
				className={cn(
					"tw-flex tw-min-h-[132px] tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-[10px] tw-border tw-border-dashed tw-border-border tw-bg-bg tw-px-4 tw-py-6 tw-text-center tw-transition-colors hover:tw-border-teal",
					dragging && "tw-border-teal tw-bg-teal-tint",
					error && "tw-border-red",
					disabled && "tw-cursor-not-allowed tw-opacity-60",
					className
				)}
			>
				<span className="tw-text-[14px] tw-font-medium tw-text-text">{file ? file.name : "Drag a file here, or click to browse"}</span>
				<span className="tw-text-[12px] tw-text-text-soft">{file ? "Click to choose a different file" : accept ?? "Spreadsheet files"}</span>

				<input
					id={inputId}
					type="file"
					accept={accept}
					disabled={disabled}
					className="tw-hidden"
					onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
				/>
			</label>
		</Field>
	);
};

export default FileDropzone;
